/**
 * Impersonation Banner Component
 * 
 * Displays a prominent banner when a Super Admin is impersonating another user.
 * Provides visual indication of impersonation state and controls to stop impersonation.
 * 
 * Features:
 * - Clear visual indication of impersonation mode
 * - Shows both original and impersonated user information
 * - One-click stop impersonation functionality
 * - Accessible design with proper ARIA labels
 */

import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertTriangle, User, ArrowLeft } from 'lucide-react';

export function ImpersonationBanner() {
  const { 
    isImpersonating, 
    originalUser, 
    impersonatedUser, 
    stopImpersonation 
  } = useAuth();

  const handleStopImpersonation = async () => {
    const success = await stopImpersonation();
    if (success) {
      // Optionally reload the page or redirect to refresh state
      window.location.reload();
    }
  };

  if (!isImpersonating || !originalUser || !impersonatedUser) {
    return null;
  }

  return (
    <div 
      className="bg-yellow-100 text-yellow-800 py-2 px-4 border-b border-yellow-300 sticky top-0 z-50"
      role="banner"
      aria-label="Impersonation Mode Active"
    >
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <span className="text-lg">⚠️</span>
          <div className="flex items-center space-x-2 text-sm font-medium">
            <span>You're impersonating {impersonatedUser.username} ({impersonatedUser.role})</span>
          </div>
        </div>
        
        <Button
          onClick={handleStopImpersonation}
          variant="outline"
          size="sm"
          className="border-yellow-600 text-yellow-800 hover:bg-yellow-200 transition-colors"
          aria-label="Stop impersonation and return to original user"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Stop
        </Button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;